import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import {
  DocumentUnderstandingAgentRelationshipOverride,
  useDocumentUnderstandingAgentRelationships,
} from 'services/repositories/documentUnderstandingAgent/documentUnderstandingAgent';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { LoadingPage } from 'components/Loading/LoadingPage';

import { hasConnectionOverrides } from '../../helpers';
import { CustomInstructionsCell } from './components/cells/CustomInstructionsCell';
import { PartnerCell } from './components/cells/PartnerCell';
import { PriceStrategyCell } from './components/cells/PriceStrategyCell';
import { UnitsOfMeasureCell } from './components/cells/UnitsOfMeasureCell';
import { ConnectionDrawer } from './components/ConnectionDrawer';

const CONNECTION_PARAM = 'connection';

export const ByConnectionsTab = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: overrides, isLoading, isError, refetch } = useDocumentUnderstandingAgentRelationships();

  const visibleOverrides = (overrides ?? []).filter(hasConnectionOverrides);

  const connectionParam = searchParams.get(CONNECTION_PARAM);
  const selectedId = connectionParam ? Number(connectionParam) : null;
  const selectedOverride =
    selectedId !== null ? (visibleOverrides.find((override) => override.id === selectedId) ?? null) : null;

  const openOverride = (id: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(CONNECTION_PARAM, String(id));
    setSearchParams(nextParams);
  };

  const closeOverride = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(CONNECTION_PARAM);
    setSearchParams(nextParams);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isError || !overrides) {
    return (
      <EmptyState
        icon={<ExclamationCircleIcon />}
        title={t('settings:documentUnderstandingAgent.byConnections.error.title')}
        description={t('settings:documentUnderstandingAgent.byConnections.error.description') ?? undefined}
        ctaText={t('settings:documentUnderstandingAgent.byConnections.error.retry') ?? undefined}
        ctaOnClick={() => refetch()}
      />
    );
  }

  const tableHeaders: Array<TableHeader> = [
    { label: t('partners:list.myPartners.headers.name'), key: 'partner' },
    { label: t('settings:documentUnderstandingAgent.global.priceStrategy.title'), key: 'price-strategy' },
    { label: t('settings:documentUnderstandingAgent.global.unitsOfMeasure.title'), key: 'units-of-measure' },
    { label: t('settings:documentUnderstandingAgent.global.customInstructions.title'), key: 'custom-instructions' },
    { label: '', key: 'chevron' },
  ];

  const buildRow = (override: DocumentUnderstandingAgentRelationshipOverride): Array<TableItem> => [
    {
      key: `partner-${override.id}`,
      element: <PartnerCell logoUrl={override.counterparty.logo_url} name={override.counterparty.name} />,
    },
    {
      key: `price-strategy-${override.id}`,
      element: <PriceStrategyCell strategy={override.configuration.prices_extraction_strategy} />,
    },
    {
      key: `units-of-measure-${override.id}`,
      element: <UnitsOfMeasureCell units={override.configuration.preferred_units_of_measure} />,
    },
    {
      key: `custom-instructions-${override.id}`,
      className: 'max-w-xs truncate',
      element: <CustomInstructionsCell instructions={override.configuration.custom_instructions} />,
    },
    {
      key: `chevron-${override.id}`,
      element: <ChevronRightIcon className="size-4 shrink-0 text-gray-400" />,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <ListTable<DocumentUnderstandingAgentRelationshipOverride>
        headers={tableHeaders}
        data={visibleOverrides}
        isLoading={false}
        rowBuilder={buildRow}
        onRowClick={(override) => openOverride(override.id)}
      />

      <p className="text-xs text-gray-400">
        {t('settings:documentUnderstandingAgent.byConnections.footer', { count: visibleOverrides.length })}
      </p>

      <ConnectionDrawer override={selectedOverride} onClose={closeOverride} />
    </div>
  );
};
